import { type ComponentType, type ReactElement } from 'react';
import Block from '@mui/icons-material/Block';
import Calculate from '@mui/icons-material/Calculate';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import Co2 from '@mui/icons-material/Co2';
import DataObject from '@mui/icons-material/DataObject';
import ElectricBolt from '@mui/icons-material/ElectricBolt';
import Forest from '@mui/icons-material/Forest';
import Functions from '@mui/icons-material/Functions';
import List from '@mui/icons-material/List';
import Numbers from '@mui/icons-material/Numbers';
import Paid from '@mui/icons-material/Paid';
import Password from '@mui/icons-material/Password';
import Percent from '@mui/icons-material/Percent';
import Scale from '@mui/icons-material/Scale';
import Straighten from '@mui/icons-material/Straighten';
import TableChart from '@mui/icons-material/TableChart';
import TextFields from '@mui/icons-material/TextFields';
import ToggleOn from '@mui/icons-material/ToggleOn';
import VerticalAlignBottom from '@mui/icons-material/VerticalAlignBottom';
import VerticalAlignCenter from '@mui/icons-material/VerticalAlignCenter';
import VerticalAlignTop from '@mui/icons-material/VerticalAlignTop';
import WaterDrop from '@mui/icons-material/WaterDrop';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import Icon from 'components/Icon';
import {
   type AggregationFunctionEnum,
   ApprovalStatus,
   type ConsolidationFunctionEnum,
   EntityRelationType,
   KPIFieldType,
   type KPIFieldTypeEnum,
   OrganisationType,
} from 'utils/enum';

export function getEntityKindIcon(kind: string) {
   let icon: string | undefined;

   switch (kind) {
      case 'all':
         icon = 'search';
         break;
      case 'emissionfactor':
         icon = 'calculator-simple';
         break;
      case 'kpiarea':
      case 'kpi':
      case 'kpisub':
      case 'kpicontent':
      case 'kpifield':
         icon = 'pen-field';
         break;
      case 'businessactivity':
         icon = 'briefcase';
         break;
      case 'taxonomyactivity':
         icon = 'leaf';
         break;
      case 'emissioncategory':
         icon = 'smoke';
         break;
      case 'organisation':
         icon = 'sitemap';
         break;
      case 'chart':
         icon = 'chart-column';
         break;
      case 'report':
         icon = 'book';
         break;
      case 'reportdocument':
         icon = 'file-word';
         break;
      case 'reportingstandard':
         icon = 'file-certificate';
         break;
      case 'task':
         icon = 'list-check';
         break;
      case 'user':
         icon = 'user';
         break;
      case 'group':
         icon = 'user-group';
         break;
      case 'currency':
      case 'currencyrate':
         icon = 'coins';
         break;
      case 'footprint':
         icon = 'shoe-prints';
         break;
      case 'footprintcalculation':
      case 'fpcalculation':
      case 'footprintsub':
         icon = 'calculator';
         break;
      case 'footprintcalculationfield':
      case 'fpcalculationfield':
         icon = 'shoe-prints';
         break;
      case 'refArea':
         icon = 'bullseye';
         break;
      case 'attachment':
      case 'taxonomyresponseattachment':
         icon = 'attachment';
         break;
      case 'taxonomycriteriaresponse':
         icon = 'leaf';
         break;
      case 'searchHistory':
         icon = 'history';
         break;
      case 'reductionplan':
         icon = 'chart-line-down';
         break;
      case 'reductionplantarget':
         icon = 'crosshairs';
         break;
      default:
         break;
   }

   return <Icon icon={icon} fontSize="small" />;
}

export function getOrganisationIcon(organisationType: keyof typeof OrganisationType) {
   let icon: string | undefined;

   switch (organisationType) {
      case OrganisationType.REGION:
         icon = 'globe';
         break;
      case OrganisationType.COMMUNE:
         icon = 'users';
         break;
      case OrganisationType.AREA:
         icon = 'layer-group';
         break;
      case OrganisationType.PRODUCT:
         icon = 'tube-chemistry';
         break;
      case OrganisationType.PORTFOLIO:
         icon = 'folder-open';
         break;
      case OrganisationType.INVESTMENT:
         icon = 'hand-holding-usd';
         break;
      case OrganisationType.ORGANIZATION:
      default:
         icon = 'building';
         break;
   }

   return <Icon icon={icon} style={{ verticalAlign: 'middle' }} />;
}

export function getEntityRelationIcon(entityRelation: keyof typeof EntityRelationType) {
   let icon: string | undefined;

   switch (entityRelation) {
      case EntityRelationType.CUSTOMER:
         icon = 'truck-field';
         break;
      case EntityRelationType.SUPPLIER:
         icon = 'user-check';
         break;
      case EntityRelationType.SUBSIDIARY:
      default:
         icon = 'building';
         break;
   }

   return <Icon icon={icon} style={{ verticalAlign: 'middle' }} />;
}

export function getProviderIcon(provider: string) {
   switch (provider) {
      case 'GOOGLE':
         return <Icon icon="google" fontSize={40} />;
      case 'AZURE':
         return <Icon icon="microsoft" />;
      case 'SLACK':
         return <Icon icon="slack" />;
      case 'GITHUB':
         return <Icon icon="github" fontSize={20} />;
      case 'OKTA':
         return <Icon icon="okta" />;
      case 'WEBAUTHN':
         return <Icon icon="webauthn" />;
      case 'SAML':
         return <Icon icon="saml" />;
      case 'OPENID':
         return <Icon icon="openid" />;
      default:
         return <Password />;
   }
}

export function getAuditStatusIcon(auditStatus: string): string {
   switch (auditStatus) {
      case ApprovalStatus.REVIEWED:
         return 'check_circle_outlined';
      case ApprovalStatus.APPROVED:
         return 'check_circle';
      case ApprovalStatus.ARCHIVED:
         return 'inventory_2';
      case 'UNARCHIVE':
         return 'unarchive';
      case 'UNAPPROVE':
         return 'thumbs-down';
      case 'UNREVIEW':
         return 'thumbs-down';
      case 'REVIEW':
         return 'eye';
      case 'APPROVE':
         return 'thumbs-up';
      case 'ARCHIVE':
         return 'box-archive';
      case ApprovalStatus.NO_VERIFICATION_NEEDED:
         return 'verified';
      case ApprovalStatus.IN_PROGRESS:
      default:
         return 'hardware';
   }
}

export function getKPIFieldTypeIcon(kpiFieldType: KPIFieldTypeEnum) {
   switch (kpiFieldType) {
      case KPIFieldType.DATA:
         return <DataObject />;
      case KPIFieldType.DATE:
         return <CalendarMonth />;
      case KPIFieldType.DISTANCE:
         return <Straighten />;
      case KPIFieldType.ELECTRICITY:
         return <ElectricBolt />;
      case KPIFieldType.EMISSION_SELECT:
         return <Co2 />;
      case KPIFieldType.EMISSIONS:
         return <Co2 />;
      case KPIFieldType.FORMULA:
         return <Calculate />;
      case KPIFieldType.MEASUREMENT:
         return <Straighten />;
      case KPIFieldType.NUMBER:
         return <Numbers />;
      case KPIFieldType.PERCENTAGE:
         return <Percent />;
      case KPIFieldType.PRICE:
         return <Paid />;
      case KPIFieldType.SELECT:
         return <List />;
      case KPIFieldType.SURFACE:
         return <Straighten />;
      case KPIFieldType.SWITCH:
         return <ToggleOn />;
      case KPIFieldType.TABLE:
         return <TableChart />;
      case KPIFieldType.TEXT:
         return <TextFields />;
      case KPIFieldType.TEXTAREA:
         return <TextFields />;
      case KPIFieldType.TIME:
         return <CalendarMonth />;
      case KPIFieldType.VOLUME:
         return <WaterDrop />;
      case KPIFieldType.WEIGHT:
         return <Scale />;
      case KPIFieldType.WOOD:
         return <Forest />;
      default:
         return undefined;
   }
}

const consolidationFunctionSymbolComponents: Record<ConsolidationFunctionEnum | AggregationFunctionEnum, ComponentType<SvgIconProps>> = {
   sum: Functions,
   sum_row: Functions,
   sum_row_col: Functions,
   avg: VerticalAlignCenter,
   min: VerticalAlignBottom,
   max: VerticalAlignTop,
   pct: Percent,
   wavg: Scale,
   relation: Percent,
   none: Block,
};

export function getConsolidationFunctionSymbol(symbol: ConsolidationFunctionEnum | AggregationFunctionEnum, props?: SvgIconProps) {
   const Symbol = consolidationFunctionSymbolComponents[symbol];

   return <Symbol {...props} />;
}

export const consolidationFunctionSymbols: Record<ConsolidationFunctionEnum | AggregationFunctionEnum, ReactElement<any>> = {
   sum: <Functions />,
   sum_row: <Functions />,
   sum_row_col: <Functions />,
   avg: <VerticalAlignCenter />,
   min: <VerticalAlignBottom />,
   max: <VerticalAlignTop />,
   pct: <Percent />,
   wavg: <Scale />,
   relation: <Percent />,
   none: <Block />,
};
